import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface RichTextEditorProps {
  value: string;
  onChange: (content: string) => void;
  label?: string;
  placeholder?: string;
  readOnly?: boolean;
  minHeight?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  label,
  placeholder = 'Enter your text here...',
  readOnly = false,
  minHeight = '200px',
}) => {
  const modules = {
    toolbar: readOnly
      ? false
      : [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          ['blockquote', 'code-block'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['link'],
          ['clean'],
        ],
  };

  const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'code-block',
    'list',
    'bullet',
    'link',
  ];

  return (
    <Box sx={{ width: '100%' }}>
      {label && (
        <Typography
          sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 1, color: '#666' }}
        >
          {label}
        </Typography>
      )}
      <Box
        sx={{
          border: '1px solid #ccc',
          borderRadius: '4px',
          overflow: 'hidden',
          backgroundColor: readOnly ? '#f5f5f5' : '#fff',
          '& .ql-toolbar': {
            borderBottom: '1px solid #ccc',
            backgroundColor: '#fafafa',
            padding: '8px 4px',
            '& .ql-stroke': {
              stroke: '#333',
            },
            '& .ql-fill': {
              fill: '#333',
            },
            '& .ql-picker-label': {
              color: '#333',
            },
            '& button:hover, & button.ql-active, & button:focus, & button:active': {
              color: '#1976d2',
              '& .ql-stroke': {
                stroke: '#1976d2',
              },
              '& .ql-fill': {
                fill: '#1976d2',
              },
            },
          },
          '& .ql-container': {
            fontSize: '0.875rem',
            fontFamily: 'inherit',
            '& .ql-editor': {
              minHeight: minHeight,
              padding: '12px',
              color: '#333',
              '&.ql-blank::before': {
                color: '#999',
                fontSize: '0.875rem',
              },
              '& h1': { fontSize: '2em', margin: '0.67em 0' },
              '& h2': { fontSize: '1.5em', margin: '0.75em 0' },
              '& h3': { fontSize: '1.17em', margin: '0.83em 0' },
              '& blockquote': {
                borderLeft: '4px solid #1976d2',
                paddingLeft: '1rem',
                marginLeft: 0,
                color: '#666',
                fontStyle: 'italic',
              },
              '& pre': {
                backgroundColor: '#f5f5f5',
                padding: '10px',
                borderRadius: '4px',
                overflow: 'auto',
              },
              '& code': {
                backgroundColor: '#f0f0f0',
                padding: '2px 4px',
                borderRadius: '3px',
                fontFamily: 'monospace',
              },
              '& a': {
                color: '#1976d2',
                textDecoration: 'underline',
                cursor: 'pointer',
              },
            },
          },
        }}
      >
        <ReactQuill
          theme={readOnly ? 'bubble' : 'snow'}
          value={value || ''}
          onChange={onChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          readOnly={readOnly}
        />
      </Box>
    </Box>
  );
};

export default RichTextEditor;
